import { blue, purple, red } from "@mui/material/colors";
import { lighten } from "@mui/material/styles";
import { CSSToRgb, hexToRgb } from "common/utilities/colors/colors";
import type { Color } from "common/utilities/colors/colors.types";
import { valuePositionInBracket } from "common/utilities/core/number";
import { SequenceType } from "sequenceAnalyser/api/sequenceAnalyserApiClient";

import { colorOpacityForPercentage } from "./colorCalculations/colorCalculations";

export interface ResiduesGroup {
  name: string;
  residues: string[];
  type: "NU" | "AA";
}

const colorsResiduesGroups: ResiduesGroup[] = [
  { name: "Aliphatic", residues: ["A", "G", "I", "L", "P", "V"], type: "AA" },
  { name: "Aromatic", residues: ["F", "W", "Y"], type: "AA" },
  { name: "Sulfur", residues: ["C", "M"], type: "AA" },
  { name: "Hydroxyl", residues: ["S", "T"], type: "AA" },
  { name: "Basic", residues: ["R", "H", "K"], type: "AA" },
  { name: "Acidic", residues: ["D", "E"], type: "AA" },
  { name: "Amide", residues: ["N", "Q"], type: "AA" },
  { name: "Purine", residues: ["A", "G"], type: "NU" },
  { name: "Pyrimidine", residues: ["C", "T"], type: "NU" },
  { name: "A-T", residues: ["A", "T"], type: "NU" },
  { name: "G-C", residues: ["G", "C"], type: "NU" },
];

export function getColorsResiduesGroups(sequenceType: SequenceType) {
  if (sequenceType === SequenceType.dna) {
    return colorsResiduesGroups.filter((m) => m.type === "NU");
  }
  if (sequenceType === SequenceType.protein) {
    return colorsResiduesGroups.filter((m) => m.type === "AA");
  }
  return [];
}

interface Molecule {
  name: string;
  residue: string;
  type: "NU" | "AA";
}

const molecules: Molecule[] = [
  { name: "Adenine", residue: "A", type: "NU" },
  { name: "Cytosine", residue: "C", type: "NU" },
  { name: "Guanine", residue: "G", type: "NU" },
  { name: "Thymine", residue: "T", type: "NU" },
  { name: "Alanine", residue: "A", type: "AA" },
  { name: "Arginine", residue: "R", type: "AA" },
  { name: "Asparagine", residue: "N", type: "AA" },
  { name: "Aspartic Acid", residue: "D", type: "AA" },
  { name: "Cysteine", residue: "C", type: "AA" },
  { name: "Glutamic Acid", residue: "E", type: "AA" },
  { name: "Glutamine", residue: "Q", type: "AA" },
  { name: "Glycine", residue: "G", type: "AA" },
  { name: "Histidine", residue: "H", type: "AA" },
  { name: "Isoleucine", residue: "I", type: "AA" },
  { name: "Leucine", residue: "L", type: "AA" },
  { name: "Lysine", residue: "K", type: "AA" },
  { name: "Methionine", residue: "M", type: "AA" },
  { name: "Phenylalanine", residue: "F", type: "AA" },
  { name: "Proline", residue: "P", type: "AA" },
  { name: "Serine", residue: "S", type: "AA" },
  { name: "Threonine", residue: "T", type: "AA" },
  { name: "Tryptophan", residue: "W", type: "AA" },
  { name: "Tyrosine", residue: "Y", type: "AA" },
  { name: "Valine", residue: "V", type: "AA" },
];

export function getMolecules(sequenceType: SequenceType) {
  if (sequenceType === SequenceType.dna) {
    return molecules.filter((m) => m.type === "NU");
  }
  if (sequenceType === SequenceType.protein) {
    return molecules.filter((m) => m.type === "AA");
  }
  return [];
}

// Selected color scheme:
// contains color for each residue and a flag to show it or not (used can decide)
export interface SelectedColorScheme {
  name: ColorSchemesName;
  infos: ColorInfos;
  conservation: number; // percentage (default should be 0, meaning all)
  topOccurrenceOnly: boolean;
  hydrophobicity?: [number, number];
}

export type ColorInfos = Record<string, ColorInfo>;

export interface ColorInfo {
  name: string;
  color?: Color;
  hydrophobicValue?: number;
  show: boolean;
}

// ColorSchemes:
// list of schemes with mapping of residues to colors for each of them
type ColorSchemes = Record<ColorSchemesName, ColorScheme>;

type ColorSchemeColors = Record<string, Color>;

type ColorSchemeHydrophobicValues = Record<string, number>;

export interface ColorScheme {
  type?: "AA" | "NU";
  colors?: ColorSchemeColors;
  hydrophobicValues?: ColorSchemeHydrophobicValues;
  hydrophobic?: number;
  hydrophilic?: number;
}

export enum ColorSchemesName {
  None = "None",
  Conservation = "Conservation",
  AminoAcid = "Amino Acid",
  Nucleotide = "Nucleotide",
  Fischer = "Fischer",
  Zappo = "Zappo",
  Taylor = "Taylor",
  HydrophobicityKyteDoolittle = "Kyte-Doolittle",
  HydrophobicityChothia = "Chothia",
  HydrophobicityEisenbergWeiss = "Eisenberg-Weiss",
  HydrophobicityEngelmanSteitz = "Engelman-Steitz",
  HydrophobicityHoppWoods = "Hopp-Woods",
  HydrophobicityJanin = "Janin",
}

export function isConservationMandatoryForSelectedColorScheme(colors: SelectedColorScheme) {
  return [ColorSchemesName.Conservation].includes(colors.name);
}
function populateColorsAndHydrophobicValues(
  values: ColorSchemeHydrophobicValues,
  hydrophobicThreshold: number,
  hydrophilicThreshold: number
) {
  const v = Object.values(values);
  const min = Math.min(...v);
  const max = Math.max(...v);

  const colors: ColorSchemeColors = Object.keys(values).reduce<ColorSchemeColors>((acc, residue) => {
    const value = values[residue];
    // min = blue 100%
    // < HydrophilicThreshold = blue lighter 0% (adjusted not to be completely transparent)
    // in between: purple
    // > hydrophobicThreshold = red lighter 0% (adjusted not to be completely transparent)
    // max = red 100%
    if (value >= hydrophobicThreshold) {
      acc[residue] = CSSToRgb(
        lighten(red[500], (100 - valuePositionInBracket(hydrophobicThreshold, max, value)) / 100 / 1.1)
      );
    } else if (value <= hydrophilicThreshold) {
      acc[residue] = CSSToRgb(
        lighten(blue[500], Math.abs(valuePositionInBracket(min, hydrophilicThreshold, value) / 100 / 1.1))
      );
    } else {
      acc[residue] = hexToRgb(purple[200]) as Color;
    }

    return acc;
  }, {});

  return {
    colors,
    hydrophobicValues: values,
    hydrophobic: hydrophobicThreshold,
    hydrophilic: hydrophilicThreshold,
  };
}

export const stopCodonColor = "#fa9151";

const colorSchemes: ColorSchemes = {
  [ColorSchemesName.None]: {},
  [ColorSchemesName.Conservation]: { type: "AA" },
  [ColorSchemesName.AminoAcid]: {
    type: "AA",
    colors: {
      default: [100, 100, 100],
      A: [127, 130, 252],
      R: [199, 200, 253],
      N: [25, 36, 166],
      D: [62, 233, 216],
      C: [95, 232, 89],
      E: [202, 254, 200],
      Q: [54, 189, 46],
      G: [170, 197, 48],
      H: [255, 253, 60],
      I: [254, 217, 56],
      L: [216, 101, 39],
      K: [230, 83, 84],
      M: [254, 235, 235],
      F: [146, 30, 31],
      P: [145, 41, 196],
      S: [240, 157, 251],
      T: [120, 20, 120],
      W: [197, 148, 43],
      Y: [200, 150, 20],
      V: [147, 100, 35],
    },
  },
  [ColorSchemesName.Nucleotide]: {
    type: "NU",
    colors: {
      A: [249, 104, 104],
      C: [20, 150, 20],
      G: [255, 222, 26],
      T: [100, 100, 255],
    },
  },
  [ColorSchemesName.Fischer]: {
    type: "NU",
    colors: {
      A: [255, 237, 237],
      C: [200, 200, 255],
      G: [127, 127, 255],
      T: [235, 80, 80],
    },
  },
  [ColorSchemesName.Zappo]: {
    type: "AA",
    colors: {
      I: [253, 199, 199],
      L: [253, 199, 199],
      W: [253, 199, 199],
      F: [252, 168, 48],
      Y: [252, 168, 48],
      V: [252, 168, 48],
      R: [146, 30, 31],
      H: [146, 30, 31],
      K: [146, 30, 31],
      D: [199, 200, 253],
      E: [199, 200, 253],
      A: [127, 127, 127],
      G: [127, 127, 127],
      S: [127, 127, 127],
      P: [145, 41, 196],
      C: [255, 253, 60],
      N: [25, 36, 166],
      Q: [25, 36, 166],
      M: [25, 36, 166],
      T: [25, 36, 166],
    },
  },
  [ColorSchemesName.Taylor]: {
    type: "AA",
    colors: {
      A: [204, 255, 0],
      R: [20, 20, 235],
      N: [110, 20, 235],
      D: [235, 20, 20],
      C: [235, 235, 20],
      E: [235, 20, 110],
      Q: [235, 20, 190],
      G: [229, 141, 20],
      H: [20, 110, 235],
      I: [110, 235, 20],
      L: [65, 235, 20],
      K: [110, 20, 235],
      M: [0, 255, 0],
      F: [20, 235, 110],
      P: [235, 190, 20],
      S: [235, 65, 20],
      T: [235, 110, 20],
      W: [20, 190, 235],
      Y: [20, 235, 190],
      V: [145, 235, 20],
    },
  },
  [ColorSchemesName.HydrophobicityKyteDoolittle]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: 1.8,
        R: -4.5,
        N: -3.5,
        D: -3.5,
        C: 2.5,
        E: -3.5,
        Q: -3.5,
        G: -0.4,
        H: -3.2,
        I: 4.5,
        L: 3.8,
        K: -3.9,
        M: 1.9,
        F: 2.8,
        P: -1.6,
        S: -0.8,
        T: -0.7,
        W: -0.9,
        Y: -1.3,
        V: 4.2,
      },
      0.7,
      -2.4
    ),
  },
  [ColorSchemesName.HydrophobicityChothia]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: -0.29,
        R: -2.71,
        N: -1.18,
        D: -1.02,
        C: 0,
        E: -0.9,
        Q: -1.53,
        G: -0.34,
        H: -9.94,
        I: 0.24,
        L: -0.12,
        K: -2.05,
        M: -0.24,
        F: 0,
        P: -0.9,
        S: -0.75,
        T: -0.71,
        W: -0.59,
        Y: -1.02,
        V: 0.09,
      },
      -0.47,
      -0.98
    ),
  },
  [ColorSchemesName.HydrophobicityEisenbergWeiss]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: 0.25,
        R: -1.8,
        N: -0.64,
        D: -0.72,
        C: 0.04,
        E: -0.62,
        Q: -0.69,
        G: 0.16,
        H: -0.4,
        I: 0.73,
        L: 0.53,
        K: -1.1,
        M: 0.26,
        F: 0.61,
        P: -0.07,
        S: -0.26,
        T: -0.18,
        W: 0.37,
        Y: 0.02,
        V: 0.54,
      },
      0.1,
      -0.51
    ),
  },
  [ColorSchemesName.HydrophobicityEngelmanSteitz]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: 1.6,
        R: -12.3,
        N: -4.8,
        D: -9.2,
        C: 2,
        E: -8.2,
        Q: -4.1,
        G: 1,
        H: -3,
        I: 3.1,
        L: 2.8,
        K: -8.8,
        M: 3.4,
        F: 3.7,
        P: -0.2,
        S: 0.6,
        T: 1.2,
        W: 1.9,
        Y: -0.7,
        V: 2.6,
      },
      1.4,
      -1.85
    ),
  },
  [ColorSchemesName.HydrophobicityHoppWoods]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: 0.5,
        R: -3,
        N: -0.2,
        D: -3,
        C: 1,
        E: -3,
        Q: -0.2,
        G: 0,
        H: 0.5,
        I: 1.8,
        L: 1.8,
        K: -3,
        M: 1.3,
        F: 2.5,
        P: 0,
        S: -0.3,
        T: 0.4,
        W: 3.4,
        Y: 2.3,
        V: 1.5,
      },
      0.75,
      -1.65
    ),
  },
  [ColorSchemesName.HydrophobicityJanin]: {
    type: "AA",
    ...populateColorsAndHydrophobicValues(
      {
        A: 0.3,
        R: -1.4,
        N: -0.5,
        D: -0.6,
        C: 0.9,
        E: -0.7,
        Q: -0.7,
        G: 0.3,
        H: -0.1,
        I: 0.7,
        L: 0.5,
        K: -1.8,
        M: 0.4,
        F: 0.5,
        P: -0.3,
        S: -0.1,
        T: -0.2,
        W: 0.3,
        Y: -0.4,
        V: 0.6,
      },
      0.1,
      -0.45
    ),
  },
};

export function getColorSchemes(sequenceType: SequenceType): Partial<ColorSchemes> {
  if (sequenceType === SequenceType.dna) {
    return {
      [ColorSchemesName.None]: colorSchemes[ColorSchemesName.None],
      [ColorSchemesName.Nucleotide]: colorSchemes[ColorSchemesName.Nucleotide],
      [ColorSchemesName.Fischer]: colorSchemes[ColorSchemesName.Fischer],
    };
  }
  if (sequenceType === SequenceType.protein) {
    return {
      [ColorSchemesName.None]: colorSchemes[ColorSchemesName.None],
      [ColorSchemesName.Conservation]: colorSchemes[ColorSchemesName.Conservation],
      [ColorSchemesName.AminoAcid]: colorSchemes[ColorSchemesName.AminoAcid],
      [ColorSchemesName.Zappo]: colorSchemes[ColorSchemesName.Zappo],
      [ColorSchemesName.Taylor]: colorSchemes[ColorSchemesName.Taylor],
      [ColorSchemesName.HydrophobicityKyteDoolittle]: colorSchemes[ColorSchemesName.HydrophobicityKyteDoolittle],
      [ColorSchemesName.HydrophobicityChothia]: colorSchemes[ColorSchemesName.HydrophobicityChothia],
      [ColorSchemesName.HydrophobicityEisenbergWeiss]: colorSchemes[ColorSchemesName.HydrophobicityEisenbergWeiss],
      [ColorSchemesName.HydrophobicityEngelmanSteitz]: colorSchemes[ColorSchemesName.HydrophobicityEngelmanSteitz],
      [ColorSchemesName.HydrophobicityHoppWoods]: colorSchemes[ColorSchemesName.HydrophobicityHoppWoods],
      [ColorSchemesName.HydrophobicityJanin]: colorSchemes[ColorSchemesName.HydrophobicityJanin],
    };
  }
  return {};
}

export const conservationColor = [25, 36, 166];

export function conservationColorBgStyle(percentage: number) {
  return `rgba(${conservationColor.join(",")}, ${colorOpacityForPercentage(percentage)}) ${percentage}%`;
}
